import * as React from "react";
import { useDocumentInfo } from "../document/document-context";
import { useFieldSet } from "../document/use-fieldset";
import { getProductsFromFieldset } from "../document/document-products";
import { useRaffleAiConfig } from "./use-raffle-ai-config";

interface DocumentPageProductCodeProps {
  children: (productCode: string | undefined) => React.ReactNode;
}

/**
 * Component that provides product code context for Raffle AI widget integration.
 *
 * Purpose:
 * - Extracts product code(s) from the document's fieldset
 * - Determines which product widget Raffle should display based on the document's product association
 *
 * Product Selection Logic:
 * The widget is shown only when there is exactly one product match between:
 * 1. The document's product associations (e.g., connect, vejledninger)
 * 2. The products configured to have Raffle widgets
 *
 * Business Rule: Strict 1-match requirement
 * - If 0 matches: No widget shown (document has no Raffle-enabled products)
 * - If 1 match: Widget shown for that product
 * - If 2+ matches: No widget shown (ambiguous which widget to display)
 *
 * @example
 * ```tsx
 * <DocumentPageProductCode>
 *   {productCode => <RaffleAiContainer productCode={productCode} />}
 * </DocumentPageProductCode>
 * ```
 */
const DocumentPageProductCode: React.FC<DocumentPageProductCodeProps> = ({
  children
}) => {
  const { hiveId, fullName } = useDocumentInfo();
  const fieldSet = useFieldSet(hiveId, fullName);
  const raffleConfig = useRaffleAiConfig();

  if (!fieldSet || !raffleConfig) {
    // Dont render children if fieldset or raffle config is not loaded
    return null;
  }

  const documentProducts = getProductsFromFieldset(fieldSet);
  const configuredRaffleProducts = raffleConfig.productWidgetMapping.map(
    mapping => mapping.productCode
  );

  const productCode = findMatchingRaffleProduct(
    documentProducts,
    configuredRaffleProducts
  );

  return <>{children(productCode)}</>;
};

export default DocumentPageProductCode;

function findMatchingRaffleProduct(
  documentProducts: string[],
  configuredRaffleProducts: string[]
) {
  const matchingProducts = documentProducts.filter(product =>
    configuredRaffleProducts.includes(product)
  );

  if (matchingProducts.length > 1) {
    console.log("Raffle widget not shown: Multiple matching products found.", {
      documentProducts,
      configuredRaffleProducts,
      matchingProducts
    });
    return undefined;
  }

  return matchingProducts.length === 1 ? matchingProducts[0] : undefined;
}
